import type { GrantOpportunitySource, GrantOpportunityStatus } from "@/api/grant_opportunities";
import { GRANT_OPPORTUNITY_STATUS_LABELS } from "@/api/grant_opportunities";
import type { GrantArtifactType } from "@/api/grant_artifacts";
import { GRANT_ARTIFACT_TYPE_LABELS } from "@/api/grant_artifacts";
import type { GrantOpportunityEvent, GrantOpportunityEventType } from "@/api/grant_opportunity_events";
import type { GrantOpportunityTaskKind } from "@/api/grant_opportunity_tasks";
import {
  formatWorkflowDeadline,
  workflowDeadlineSummary,
  workflowDeadlineTone,
} from "@/lib/deadlineDisplay";

export type { GrantOpportunityStatus };

export const GRANT_TASK_KIND_LABELS: Record<GrantOpportunityTaskKind, string> = {
  general: "Task",
  report: "Report",
  deliverable: "Deliverable",
  compliance: "Compliance",
};

export const GRANT_TASK_KIND_OPTIONS: Array<{ value: GrantOpportunityTaskKind; label: string }> =
  (Object.entries(GRANT_TASK_KIND_LABELS) as Array<[GrantOpportunityTaskKind, string]>).map(
    ([value, label]) => ({ value, label }),
  );

export function grantOpportunityStatusLabel(status: GrantOpportunityStatus): string {
  return GRANT_OPPORTUNITY_STATUS_LABELS[status];
}

export function grantOpportunityStatusPillClass(status: GrantOpportunityStatus): string {
  switch (status) {
    case "interested":
      return "bg-slate-100 text-slate-800 dark:bg-slate-800/60 dark:text-slate-200";
    case "preparing":
      return "bg-blue-100 text-blue-800 dark:bg-blue-950/50 dark:text-blue-200";
    case "submitted":
      return "bg-violet-100 text-violet-800 dark:bg-violet-950/50 dark:text-violet-200";
    case "awarded":
      return "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-200";
    case "declined":
      return "bg-red-100 text-red-800 dark:bg-red-950/50 dark:text-red-200";
    case "closed":
      return "bg-amber-100 text-amber-900 dark:bg-amber-950/50 dark:text-amber-200";
    default:
      return "bg-muted text-muted-foreground";
  }
}

export function grantOpportunitySourceLabel(source: GrantOpportunitySource): string {
  switch (source) {
    case "catalog":
      return "From catalog";
    case "regional":
      return "Regional";
    case "manual":
    default:
      return "Manual entry";
  }
}

export function formatGrantDeadline(value: string | null): string {
  return formatWorkflowDeadline(value);
}

export function grantDeadlineTone(value: string | null): "default" | "warning" | "muted" {
  return workflowDeadlineTone(value);
}

export function grantDeadlineSummary(value: string | null): string {
  return workflowDeadlineSummary(value, { emptyLabel: "No working deadline set" });
}

export const GRANT_OPPORTUNITY_STATUS_DESCRIPTIONS: Record<GrantOpportunityStatus, string> = {
  interested: "Saved on your watchlist while the team evaluates fit.",
  preparing: "Team is assembling materials and requirements.",
  submitted: "Application submitted; awaiting agency response.",
  awarded: "Grant awarded — track post-award obligations here.",
  declined: "Application was not selected.",
  closed: "No longer pursuing this opportunity.",
};

/** Active pursuit stages — excludes watchlist-only `interested`. */
export const GRANT_OPPORTUNITY_PIPELINE_STATUS_ORDER: GrantOpportunityStatus[] = [
  "preparing",
  "submitted",
  "awarded",
  "declined",
  "closed",
];

/**
 * Forward pipeline stages shown on the Progress bar (ENG-1698). `awarded` is the
 * terminal "won" state and fills the bar. `declined`/`closed` are off-ramps, not
 * further progress, so they are excluded here and get a terminal treatment instead.
 */
export const GRANT_OPPORTUNITY_FORWARD_STAGES: GrantOpportunityStatus[] = [
  "preparing",
  "submitted",
  "awarded",
];

export const GRANT_OPPORTUNITY_STATUS_ORDER: GrantOpportunityStatus[] = [
  "interested",
  ...GRANT_OPPORTUNITY_PIPELINE_STATUS_ORDER,
];

export function grantOpportunityPipelineStatus(status: GrantOpportunityStatus): GrantOpportunityStatus {
  return status === "interested" ? "preparing" : status;
}

export function grantOpportunityListMeta(opportunity: {
  watching: boolean;
  status: GrantOpportunityStatus;
}): { label: string; pillClass: string } {
  if (opportunity.watching) {
    return {
      label: "Saved",
      pillClass: "bg-slate-100 text-slate-700 dark:bg-slate-800/60 dark:text-slate-200",
    };
  }
  const status = grantOpportunityPipelineStatus(opportunity.status);
  return {
    label: grantOpportunityStatusLabel(status),
    pillClass: grantOpportunityStatusPillClass(status),
  };
}

const CLOSED_DEADLINE_STATUSES: GrantOpportunityStatus[] = [
  "submitted",
  "awarded",
  "declined",
  "closed",
];

export function isGrantOverdue(opportunity: {
  deadline_at: string | null;
  status: GrantOpportunityStatus;
}): boolean {
  if (!opportunity.deadline_at) return false;
  if (CLOSED_DEADLINE_STATUSES.includes(opportunity.status)) return false;
  return new Date(opportunity.deadline_at).getTime() < Date.now();
}

export function sortGrantOpportunitiesByDeadline<T extends { deadline_at: string | null; updated_at: string }>(
  opportunities: T[],
): T[] {
  return [...opportunities].sort((left, right) => {
    const leftDeadline = left.deadline_at ? new Date(left.deadline_at).getTime() : Number.POSITIVE_INFINITY;
    const rightDeadline = right.deadline_at ? new Date(right.deadline_at).getTime() : Number.POSITIVE_INFINITY;
    if (leftDeadline !== rightDeadline) return leftDeadline - rightDeadline;
    return new Date(right.updated_at).getTime() - new Date(left.updated_at).getTime();
  });
}

export type PipelineSummaryStat = {
  label: string;
  value: number;
  tone?: "default" | "muted" | "warning" | "success";
};

export function pipelineSummaryStats(
  opportunities: Array<{ watching: boolean; status: GrantOpportunityStatus; deadline_at: string | null }>,
): PipelineSummaryStat[] {
  const watchlistCount = opportunities.filter((opp) => opp.watching).length;
  const activeCount = opportunities.filter((opp) => !opp.watching).length;
  const preparingCount = opportunities.filter(
    (opp) => !opp.watching && opp.status === "preparing",
  ).length;
  const submittedCount = opportunities.filter(
    (opp) => !opp.watching && opp.status === "submitted",
  ).length;
  const overdueCount = opportunities.filter((opp) => isGrantOverdue(opp)).length;

  return [
    { label: "Saved", value: watchlistCount },
    { label: "Pursuing", value: activeCount },
    { label: "Preparing", value: preparingCount },
    { label: "Submitted", value: submittedCount },
    {
      label: "Overdue",
      value: overdueCount,
      tone: overdueCount > 0 ? "warning" : "default",
    },
    { label: "Total", value: opportunities.length, tone: "muted" },
  ];
}

function eventMetadataString(
  metadata: GrantOpportunityEvent["metadata"],
  key: string,
): string | null {
  const value = metadata[key];
  if (value == null) return null;
  return String(value);
}

export function formatGrantOpportunityEventSummary(event: GrantOpportunityEvent): string {
  const metadata = event.metadata;

  switch (event.event_type as GrantOpportunityEventType) {
    case "status_changed": {
      const to = eventMetadataString(metadata, "to") as GrantOpportunityStatus | null;
      return to ? `Status changed to ${grantOpportunityStatusLabel(to)}` : "Status updated";
    }
    case "watching_changed":
      return eventMetadataString(metadata, "to") === "false"
        ? "Started pursuing"
        : "Moved back to Saved";
    case "deadline_changed":
      return metadata.to ? "Working deadline updated" : "Working deadline cleared";
    case "task_created":
      return `Added milestone “${eventMetadataString(metadata, "title") ?? "Untitled"}”`;
    case "task_completed":
      return `Completed milestone “${eventMetadataString(metadata, "title") ?? "Untitled"}”`;
    case "task_reopened":
      return `Reopened milestone “${eventMetadataString(metadata, "title") ?? "Untitled"}”`;
    case "task_removed":
      return `Removed milestone “${eventMetadataString(metadata, "title") ?? "Untitled"}”`;
    case "task_assignee_changed": {
      const milestoneTitle = eventMetadataString(metadata, "title") ?? "Untitled";
      const toName = eventMetadataString(metadata, "to_name");
      if (!eventMetadataString(metadata, "to")) {
        return `Unassigned milestone “${milestoneTitle}”`;
      }
      return toName
        ? `Assigned milestone “${milestoneTitle}” to ${toName}`
        : `Updated assignee for milestone “${milestoneTitle}”`;
    }
    case "tasks_extracted": {
      const count = eventMetadataString(metadata, "count") ?? "0";
      const documentName = eventMetadataString(metadata, "document_name");
      return documentName
        ? `Added ${count} task${count === "1" ? "" : "s"} from “${documentName}”`
        : `Added ${count} task${count === "1" ? "" : "s"} from a document`;
    }
    case "plan_confirmed": {
      const milestones = eventMetadataString(metadata, "milestones") ?? "0";
      const tasks = eventMetadataString(metadata, "tasks") ?? "0";
      return `Confirmed the AI Partner plan: ${milestones} milestone${milestones === "1" ? "" : "s"}, ${tasks} task${tasks === "1" ? "" : "s"}`;
    }
    case "assignee_changed": {
      const toName = eventMetadataString(metadata, "to_name");
      if (!eventMetadataString(metadata, "to")) {
        return "Owner unassigned";
      }
      return toName ? `Assigned to ${toName}` : "Owner updated";
    }
    case "award_updated": {
      const field = eventMetadataString(metadata, "field");
      if (field === "requested_amount") return "Requested amount updated";
      if (field === "awarded_amount") return "Award amount updated";
      if (field === "award_date") return "Award date updated";
      return "Award details updated";
    }
    case "reviewer_added": {
      const reviewerName = eventMetadataString(metadata, "reviewer_name");
      return reviewerName ? `Added ${reviewerName} as reviewer` : "Reviewer added";
    }
    case "reviewer_removed": {
      const reviewerName = eventMetadataString(metadata, "reviewer_name");
      return reviewerName ? `Removed ${reviewerName} as reviewer` : "Reviewer removed";
    }
    case "contributor_added": {
      const contributorName = eventMetadataString(metadata, "contributor_name");
      return contributorName ? `Added ${contributorName} as contributor` : "Contributor added";
    }
    case "contributor_removed": {
      const contributorName = eventMetadataString(metadata, "contributor_name");
      return contributorName ? `Removed ${contributorName} as contributor` : "Contributor removed";
    }
    case "external_organization_linked": {
      const organizationName = eventMetadataString(metadata, "external_organization_name");
      return organizationName
        ? `Linked external organization ${organizationName}`
        : "External organization linked";
    }
    case "external_organization_unlinked": {
      const organizationName = eventMetadataString(metadata, "external_organization_name");
      return organizationName
        ? `Unlinked external organization ${organizationName}`
        : "External organization unlinked";
    }
    case "external_person_added": {
      const personEmail = eventMetadataString(metadata, "person_email");
      const organizationName = eventMetadataString(metadata, "external_organization_name");
      if (personEmail && organizationName) return `Added ${personEmail} to ${organizationName}`;
      return personEmail ? `Added outside person ${personEmail}` : "Outside person added";
    }
    case "external_invite_requested": {
      const personEmail = eventMetadataString(metadata, "person_email");
      return personEmail ? `Queued invite for ${personEmail}` : "Outside invite queued";
    }
    case "department_member_added": {
      const memberName = eventMetadataString(metadata, "member_name");
      const departmentName = eventMetadataString(metadata, "department_name");
      if (memberName && departmentName) return `${memberName} joined ${departmentName}`;
      return memberName ? `${memberName} joined the department` : "Department member joined";
    }
    case "department_member_removed": {
      const memberName = eventMetadataString(metadata, "member_name");
      const departmentName = eventMetadataString(metadata, "department_name");
      if (memberName && departmentName) return `${memberName} left ${departmentName}`;
      return memberName ? `${memberName} left the department` : "Department member left";
    }
    case "scope_changed": {
      const toName = eventMetadataString(metadata, "to_name");
      const fromName = eventMetadataString(metadata, "from_name");
      if (toName) return `Moved to ${toName} department`;
      if (fromName) return `Moved from ${fromName} department to the team`;
      return "Scope updated";
    }
    case "funder_portal_updated": {
      const field = eventMetadataString(metadata, "field");
      if (field === "funder_portal_name") return "Funder portal name updated";
      if (field === "funder_portal_url") return "Funder portal URL updated";
      if (field === "funder_portal_notes") return "Funder portal notes updated";
      return "Funder portal details updated";
    }
    case "ai_partner_updated": {
      const field = eventMetadataString(metadata, "field");
      const to = eventMetadataString(metadata, "to");
      if (field === "ai_partner") {
        return to === "true" ? "Converted to AI Partner" : "AI Partner removed";
      }
      if (field === "engagement_type") {
        if (to === "applying") return "Engagement set: applying for the grant";
        if (to === "supporting") return "Engagement set: supporting another applicant";
        return "Engagement type cleared";
      }
      if (field === "outside_beneficiary_name") {
        return to ? `Supporting ${to}` : "Outside beneficiary cleared";
      }
      return "AI Partner details updated";
    }
    case "artifact_generated": {
      const artifactType = eventMetadataString(metadata, "artifact_type");
      const label = artifactType ? GRANT_ARTIFACT_TYPE_LABELS[artifactType as GrantArtifactType] : null;
      return label ? `Generated ${label.toLowerCase()} draft` : "Generated artifact draft";
    }
    default:
      return "Activity updated";
  }
}

export function formatGrantCurrency(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return "—";
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);
}
